import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import {
  Plus, Pencil, Archive, RotateCcw, ChevronUp, ChevronDown, ChevronLeft, Lock, FileUp, Link2, Search, ClipboardList,
} from "lucide-react";

const FIELD_TYPES = [
  ["text", "Short text"], ["textarea", "Long text"], ["number", "Number"], ["currency", "Currency (£)"],
  ["date", "Date"], ["email", "Email"], ["phone", "Phone"], ["yes_no", "Yes / No"],
  ["select", "Dropdown (choose one)"], ["multiselect", "Multi-select (choose many)"], ["checkbox", "Checkbox (single)"],
  ["address", "Address"], ["secure_text", "Secure text (sensitive)"], ["file", "File upload"],
  ["people", "People / individuals (repeatable)"],
];
const ENTITY_OPTS = [["limited_company", "Limited Company"], ["sole_trader", "Sole Trader"], ["partnership", "Partnership"], ["llp", "LLP"]];
const CANONICAL = ["", "legal_name", "trading_name", "entity_type", "company_number", "registered_office", "year_end", "industry", "phone", "utr"];
const OPS = [["eq", "equals"], ["ne", "does not equal"], ["contains", "contains"], ["selected", "is selected"]];
const NEEDS_OPTIONS = new Set(["select", "multiselect"]);
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const BLANK = {
  label: "", section: "business", field_type: "text", entity_types: ["*"], service_keys: [],
  required: false, help: "", sensitive: false, options: [], conditions: null, canonical: "",
  significant: false, document: false, active: true,
};

export default function OnboardingQuestions() {
  const { id: serviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["super_admin", "pricing_admin"].includes(user?.role);
  const serviceMode = !!serviceId;

  const [service, setService] = useState(null);
  const [sections, setSections] = useState([]);
  const [services, setServices] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [query, setQuery] = useState("");
  const [edit, setEdit] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);

  const serviceKey = service?.internal_name;

  const load = useCallback(async () => {
    const [secs, svcs, all] = await Promise.all([
      api.get("/onboarding-sections").then((r) => r.data),
      api.get("/services").then((r) => r.data).catch(() => []),
      api.get("/onboarding-questions").then((r) => r.data),
    ]);
    setSections(secs);
    setServices(svcs);
    setAllQuestions(all);
    let svc = null;
    if (serviceMode) {
      svc = svcs.find((s) => s.id === serviceId) || null;
      setService(svc);
    }
    const key = svc?.internal_name;
    setQuestions(serviceMode && key ? all.filter((q) => (q.service_keys || []).includes(key)) : all);
  }, [serviceId, serviceMode]);

  useEffect(() => { load(); }, [load]);

  const svcName = (k) => services.find((s) => s.internal_name === k)?.client_name || k;

  const filtered = useMemo(() => {
    let qs = questions.filter((q) => (showArchived ? true : q.active !== false));
    const t = query.trim().toLowerCase();
    if (t) qs = qs.filter((q) => (q.label + " " + q.key + " " + (q.help || "")).toLowerCase().includes(t));
    return qs;
  }, [questions, showArchived, query]);

  const bySection = useMemo(() => {
    const map = {};
    for (const q of filtered) (map[q.section] ||= []).push(q);
    return map;
  }, [filtered]);

  const save = async (q) => {
    if (!q.label.trim()) { toast.error("Question label is required"); return; }
    const payload = { ...q, canonical: q.canonical || null };
    if (q.document) payload.field_type = "file";
    if (!NEEDS_OPTIONS.has(payload.field_type)) payload.options = [];
    try {
      if (q.id) await api.put(`/onboarding-questions/${q.id}`, payload);
      else {
        if (serviceMode && serviceKey && !payload.service_keys.includes(serviceKey)) payload.service_keys = [...payload.service_keys, serviceKey];
        await api.post("/onboarding-questions", payload);
      }
      toast.success(q.id ? "Question updated" : "Question added");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const deactivate = async (q) => {
    if (q.active === false) { await api.put(`/onboarding-questions/${q.id}`, { ...q, active: true }); toast.success("Question restored"); load(); return; }
    if (!(await confirmDialog({ title: "Deactivate question?", description: "It will no longer appear on new onboarding forms. Existing in-progress forms keep the question they were created with.", confirmText: "Deactivate" }))) return;
    await api.delete(`/onboarding-questions/${q.id}`); toast.success("Question deactivated"); load();
  };

  const move = async (secKey, qid, dir) => {
    const visible = [...(bySection[secKey] || [])];
    const idx = visible.findIndex((x) => x.id === qid); const to = idx + dir;
    if (to < 0 || to >= visible.length) return;
    const [m] = visible.splice(idx, 1); visible.splice(to, 0, m);
    const visibleIds = new Set(visible.map((x) => x.id));
    const byOrder = (a, b) => (a.display_order || 0) - (b.display_order || 0);
    const order = [];
    for (const s of sections) {
      const secAll = allQuestions.filter((q) => q.section === s.key).sort(byOrder);
      if (s.key === secKey) {
        order.push(...visible.map((x) => x.id));
        order.push(...secAll.filter((q) => !visibleIds.has(q.id)).map((q) => q.id));
      } else {
        order.push(...secAll.map((q) => q.id));
      }
    }
    const known = new Set(sections.map((s) => s.key));
    order.push(...allQuestions.filter((q) => !known.has(q.section)).map((q) => q.id));
    try { await api.post("/onboarding-questions/reorder", { ids: order }); load(); }
    catch { toast.error("Could not reorder"); load(); }
  };

  const toggleAttach = async (q, attach) => {
    try { await api.post(`/onboarding-questions/${q.id}/service`, { service_key: serviceKey, attach }); load(); }
    catch { toast.error("Could not update"); }
  };

  const unattached = allQuestions.filter((q) => q.active !== false && !(q.service_keys || []).includes(serviceKey));

  return (
    <div>
      <PageHeader
        title={serviceMode ? `Onboarding — ${service?.client_name || "Service"}` : "Onboarding Questions"}
        subtitle={serviceMode ? "Questions clients answer for this service after signing." : "Questions clients answer after signing, reused across services and entity types."}
        accent="teal" Icon={ClipboardList}
        actions={serviceMode
          ? <Button variant="outline" size="sm" onClick={() => navigate("/services")} data-testid="back-to-services"><ChevronLeft className="w-4 h-4 mr-1" />Catalogue</Button>
          : <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-admin"><ChevronLeft className="w-4 h-4 mr-1" />Settings</Button>} />
      <div className="p-4 md:p-8 max-w-[1100px]">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="relative max-w-xs w-full">
            <Search className="w-4 h-4 text-cass-slate absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search questions…" className="pl-9" data-testid="oq-search" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-cass-slate cursor-pointer"><Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="oq-show-archived" /> Show inactive</label>
            {canEdit && serviceMode && <Button variant="outline" onClick={() => setAttachOpen(true)} data-testid="oq-attach-existing"><Link2 className="w-4 h-4 mr-1" />Reuse existing</Button>}
            {canEdit && <Button className="bg-cass-navy text-white" onClick={() => setEdit({ ...BLANK, service_keys: serviceMode && serviceKey ? [serviceKey] : [] })} data-testid="oq-new"><Plus className="w-4 h-4 mr-1" />New question</Button>}
          </div>
        </div>

        {sections.map((sec) => {
          const items = bySection[sec.key] || [];
          if (sec.key === "review") return null;
          if (!items.length) return null;
          return (
            <div key={sec.key} className="mb-6" data-testid={`oq-section-${sec.key}`}>
              <h3 className="font-alt font-semibold text-cass-navy mb-2">{sec.title} <Badge variant="secondary" className="ml-1">{items.length}</Badge></h3>
              <div className="space-y-2">
                {items.map((q, i) => (
                  <div key={q.id} className={`flex items-start justify-between gap-3 p-3 rounded-md border border-border bg-white ${q.active === false ? "opacity-60" : ""}`} data-testid={`oq-row-${q.key}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-cass-navy flex items-center gap-2 flex-wrap">
                        {q.label}
                        {q.required && <Badge className="text-[10px] bg-cass-teal">Required</Badge>}
                        {q.sensitive && <Badge variant="outline" className="text-[10px] text-cass-slate"><Lock className="w-3 h-3 mr-0.5" />Sensitive</Badge>}
                        {q.document && <Badge variant="outline" className="text-[10px] text-cass-slate"><FileUp className="w-3 h-3 mr-0.5" />Document</Badge>}
                        {q.conditions?.rules?.length > 0 && <Badge variant="outline" className="text-[10px] text-cass-slate">Conditional</Badge>}
                        {q.active === false && <Badge variant="outline" className="text-[10px] text-cass-slate">Inactive</Badge>}
                      </div>
                      <div className="text-xs text-cass-slate mt-0.5">
                        {FIELD_TYPES.find((f) => f[0] === q.field_type)?.[1] || q.field_type}
                        {" · "}{(q.entity_types || []).includes("*") ? "All entities" : (q.entity_types || []).map((e) => ENTITY_OPTS.find((x) => x[0] === e)?.[1] || e).join(", ")}
                        {(q.service_keys || []).length ? ` · ${q.service_keys.map(svcName).join(", ")}` : " · All services"}
                        {q.canonical ? ` · updates “${q.canonical}”` : ""}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => move(sec.key, q.id, -1)} disabled={i === 0} data-testid={`oq-up-${q.key}`}><ChevronUp className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => move(sec.key, q.id, 1)} disabled={i === items.length - 1} data-testid={`oq-down-${q.key}`}><ChevronDown className="w-4 h-4" /></Button>
                        {serviceMode && <Button size="sm" variant="ghost" title="Remove from this service" onClick={() => toggleAttach(q, false)} data-testid={`oq-detach-${q.key}`}><Link2 className="w-4 h-4" /></Button>}
                        <Button size="sm" variant="outline" onClick={() => setEdit({ ...q, canonical: q.canonical || "" })} data-testid={`oq-edit-${q.key}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deactivate(q)} data-testid={`oq-archive-${q.key}`}>{q.active === false ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="text-sm text-cass-slate py-8" data-testid="oq-empty">No questions {serviceMode ? "assigned to this service" : "yet"}.</div>}
      </div>

      <QuestionEditor edit={edit} setEdit={setEdit} save={save} sections={sections} services={services} questions={allQuestions} />

      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="oq-attach-dialog">
          <DialogHeader><DialogTitle>Reuse an existing question</DialogTitle><DialogDescription>Attach a question that already exists elsewhere — the same question, no duplicate.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {unattached.length === 0 && <p className="text-sm text-cass-slate">Every active question is already assigned to this service.</p>}
            {unattached.map((q) => (
              <div key={q.id} className="flex items-center justify-between p-2.5 rounded-md border border-border" data-testid={`oq-attach-${q.key}`}>
                <div className="min-w-0"><div className="text-sm font-medium text-cass-navy truncate">{q.label}</div><div className="text-xs text-cass-slate">{sections.find((s) => s.key === q.section)?.title}</div></div>
                <Button size="sm" variant="outline" onClick={() => { toggleAttach(q, true); }} data-testid={`oq-attach-btn-${q.key}`}>Add</Button>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAttachOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuestionEditor({ edit, setEdit, save, sections, services, questions }) {
  if (!edit) return null;
  const set = (patch) => setEdit((e) => ({ ...e, ...patch }));
  const entityAll = (edit.entity_types || []).includes("*");
  const toggleEntity = (val) => {
    let ets = entityAll ? [] : [...(edit.entity_types || [])];
    ets = ets.includes(val) ? ets.filter((x) => x !== val) : [...ets, val];
    set({ entity_types: ets.length ? ets : ["*"] });
  };
  const toggleService = (key) => {
    const cur = new Set(edit.service_keys || []);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    set({ service_keys: [...cur] });
  };
  const optsText = (edit.options || []).map((o) => o[1]).join("\n");
  const setOptsText = (t) => set({ options: t.split("\n").map((s) => s.trim()).filter(Boolean).map((l) => [slug(l), l]) });

  const conds = edit.conditions || { op: "and", rules: [] };
  const setConds = (c) => set({ conditions: c.rules.length ? c : null });
  const otherQuestions = questions.filter((q) => q.id !== edit.id);

  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="oq-editor">
        <DialogHeader><DialogTitle>{edit.id ? "Edit question" : "New question"}</DialogTitle><DialogDescription>Configure how this question appears on the client onboarding form.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Question label *</Label><Input value={edit.label} onChange={(e) => set({ label: e.target.value })} placeholder="e.g. What accounting software do you use?" data-testid="oq-f-label" /></div>
          <div><Label>Help text (optional)</Label><Input value={edit.help || ""} onChange={(e) => set({ help: e.target.value })} placeholder="Shown under the question" data-testid="oq-f-help" /></div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Section</Label>
              <Select value={edit.section} onValueChange={(v) => set({ section: v })}>
                <SelectTrigger data-testid="oq-f-section"><SelectValue /></SelectTrigger>
                <SelectContent>{sections.filter((s) => s.key !== "review").map((s) => <SelectItem key={s.key} value={s.key}>{s.title}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Answer type</Label>
              <Select value={edit.document ? "file" : edit.field_type} onValueChange={(v) => set({ field_type: v })} disabled={edit.document}>
                <SelectTrigger data-testid="oq-f-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((f) => <SelectItem key={f[0]} value={f[0]}>{f[1]}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>

          {NEEDS_OPTIONS.has(edit.field_type) && !edit.document && (
            <div><Label>Choices (one per line)</Label><Textarea rows={4} value={optsText} onChange={(e) => setOptsText(e.target.value)} placeholder={"Cloud software\nSpreadsheets\nPaper"} data-testid="oq-f-options" /></div>
          )}

          <div>
            <Label>Applies to entity types</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              <button type="button" onClick={() => set({ entity_types: ["*"] })} className={`px-3 py-1.5 rounded-full text-sm border ${entityAll ? "bg-cass-teal text-white border-cass-teal" : "bg-white text-cass-navy border-border"}`} data-testid="oq-f-entity-all">All</button>
              {ENTITY_OPTS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => toggleEntity(v)} className={`px-3 py-1.5 rounded-full text-sm border ${!entityAll && (edit.entity_types || []).includes(v) ? "bg-cass-teal text-white border-cass-teal" : "bg-white text-cass-navy border-border"}`} data-testid={`oq-f-entity-${v}`}>{l}</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Applies to services <span className="text-xs text-cass-slate font-normal">(none = shown for every service)</span></Label>
            <div className="mt-1.5 max-h-40 overflow-y-auto border border-border rounded-md p-2 grid grid-cols-2 gap-1">
              {services.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm text-cass-navy cursor-pointer py-0.5">
                  <input type="checkbox" checked={(edit.service_keys || []).includes(s.internal_name)} onChange={() => toggleService(s.internal_name)} data-testid={`oq-f-svc-${s.internal_name}`} />
                  <span className="truncate">{s.client_name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
            <label className="flex items-center gap-2 text-sm text-cass-navy cursor-pointer"><Switch checked={!!edit.required} onCheckedChange={(v) => set({ required: v })} data-testid="oq-f-required" /> Required</label>
            <label className="flex items-center gap-2 text-sm text-cass-navy cursor-pointer"><Switch checked={!!edit.sensitive} onCheckedChange={(v) => set({ sensitive: v })} data-testid="oq-f-sensitive" /> Sensitive (encrypt & mask)</label>
            <label className="flex items-center gap-2 text-sm text-cass-navy cursor-pointer"><Switch checked={!!edit.document} onCheckedChange={(v) => set({ document: v, field_type: v ? "file" : edit.field_type })} data-testid="oq-f-document" /> Document request (upload)</label>
            <label className="flex items-center gap-2 text-sm text-cass-navy cursor-pointer"><Switch checked={!!edit.significant} onCheckedChange={(v) => set({ significant: v })} data-testid="oq-f-significant" /> Significant change (CASS review)</label>
          </div>

          <div>
            <Label>Updates client record field (optional)</Label>
            <Select value={edit.canonical || "none"} onValueChange={(v) => set({ canonical: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="oq-f-canonical"><SelectValue /></SelectTrigger>
              <SelectContent>{CANONICAL.map((c) => <SelectItem key={c || "none"} value={c || "none"}>{c || "— none —"}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-cass-slate mt-1">If set, the answer is pre-filled from and can update this one canonical client field (ask once, reuse everywhere).</p>
          </div>

          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between">
              <Label>Only show based on another answer</Label>
              <Switch checked={conds.rules.length > 0} onCheckedChange={(v) => setConds(v ? { op: "and", rules: [{ key: otherQuestions[0]?.key || "", op: "eq", value: "" }] } : { op: "and", rules: [] })} data-testid="oq-f-conditional" />
            </div>
            {conds.rules.length > 0 && (
              <div className="mt-2 space-y-2 bg-cass-bone/50 rounded-md p-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-cass-slate">Show when</span>
                  <Select value={conds.op} onValueChange={(v) => setConds({ ...conds, op: v })}>
                    <SelectTrigger className="w-40 h-8" data-testid="oq-f-cond-op"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="and">all rules match</SelectItem><SelectItem value="or">any rule matches</SelectItem></SelectContent>
                  </Select>
                </div>
                {conds.rules.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center" data-testid={`oq-f-rule-${i}`}>
                    <Select value={r.key} onValueChange={(v) => setConds({ ...conds, rules: conds.rules.map((x, j) => j === i ? { ...x, key: v } : x) })}>
                      <SelectTrigger className="h-8"><SelectValue placeholder="Question" /></SelectTrigger>
                      <SelectContent>{otherQuestions.map((q) => <SelectItem key={q.key} value={q.key}>{q.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={r.op} onValueChange={(v) => setConds({ ...conds, rules: conds.rules.map((x, j) => j === i ? { ...x, op: v } : x) })}>
                      <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{OPS.map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-8" value={r.value ?? ""} onChange={(e) => setConds({ ...conds, rules: conds.rules.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })} placeholder="value (e.g. yes)" />
                    <Button size="sm" variant="ghost" onClick={() => setConds({ ...conds, rules: conds.rules.filter((_, j) => j !== i) })}>✕</Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setConds({ ...conds, rules: [...conds.rules, { key: otherQuestions[0]?.key || "", op: "eq", value: "" }] })} data-testid="oq-f-add-rule"><Plus className="w-3.5 h-3.5 mr-1" />Add rule</Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter><Button onClick={() => save(edit)} className="bg-cass-teal text-white" data-testid="oq-save">{edit.id ? "Save changes" : "Add question"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
